import { PARROT_API_BASE_URL } from "../config";
import { buildRequestInit, SendMessageArgs } from "./chatService";

/** Chat endpoint for streaming (AgentCore harness) agents. Baked in — see `PARROT_API_BASE_URL`. */
export const PARROT_CHAT_STREAM_ENDPOINT = `${PARROT_API_BASE_URL}/agents/chat/stream`;

export type StreamEvent =
	| { type: "token"; text: string }
	| { type: "tool_start"; id: string; name: string }
	| { type: "tool_result"; id: string; status: string; durationMs?: number };

export interface StreamDone {
	sessionId?: string;
	/** The `done` payload as sent, for persistence as raw_response. */
	raw: unknown;
}

/**
 * Yields the `data` payload of each server-sent event in `body`.
 *
 * Only `\n` and `\r\n` line endings are handled: a lone `\r` split across chunks would read
 * as two line breaks. An event left unterminated at end of stream is still yielded, against
 * the SSE spec, so a server that omits the final blank line doesn't lose its `done`.
 */
export async function* readSse(body: ReadableStream<Uint8Array>): AsyncGenerator<string> {
	const reader = body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	let data: string[] = [];

	try {
		while (true) {
			const { done, value } = await reader.read();
			buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
			const lines = buffer.split("\n");
			buffer = done ? "" : (lines.pop() ?? "");

			for (const raw of lines) {
				const line = raw.endsWith("\r") ? raw.slice(0, -1) : raw;
				if (line === "") {
					if (data.length > 0) yield data.join("\n");
					data = [];
				} else if (line.startsWith("data:")) {
					data.push(line.slice(line.startsWith("data: ") ? 6 : 5));
				}
			}

			if (done) break;
		}
		if (data.length > 0) yield data.join("\n");
	} finally {
		reader.cancel().catch(() => undefined);
	}
}

type Parsed = StreamEvent | { type: "done"; done: StreamDone } | { type: "error"; message: string } | null;

const isString = (x: unknown): x is string => typeof x === "string";

// Unknown types map to null and are dropped, so a new backend event can't break a deployed
// widget. Also dropped: `tool` (always precedes its `tool_start`) and the `tool_input` /
// `tool_output` previews, which `trace: "minimal"` shouldn't send anyway.
const parseEvent = (data: string): Parsed => {
	let raw: Record<string, unknown>;
	try {
		raw = JSON.parse(data);
	} catch {
		return null;
	}
	if (!raw || typeof raw !== "object") return null;

	switch (raw.type) {
		case "token":
			return isString(raw.text) ? { type: "token", text: raw.text } : null;
		case "tool_start":
			return isString(raw.id) && isString(raw.name) ? { type: "tool_start", id: raw.id, name: raw.name } : null;
		case "tool_result":
			return isString(raw.id)
				? {
						type: "tool_result",
						id: raw.id,
						// Missing means success, as in the harness's other clients.
						status: isString(raw.status) ? raw.status : "success",
						durationMs: typeof raw.duration_ms === "number" ? raw.duration_ms : undefined,
					}
				: null;
		case "done":
			return {
				type: "done",
				done: { sessionId: isString(raw.session_id) ? raw.session_id : undefined, raw },
			};
		case "error":
			return { type: "error", message: isString(raw.message) ? raw.message : "Unknown error" };
		default:
			return null;
	}
};

/**
 * POSTs to a streaming agent and reports each event through `onEvent`. Resolves with the
 * `done` event. Rejects on an `error` event — even if a `done` would follow — and on a
 * stream that closes without `done`, as the reply is incomplete either way.
 */
export async function streamMessage(
	args: SendMessageArgs,
	onEvent: (event: StreamEvent) => void
): Promise<StreamDone> {
	// "minimal" gets tool start/result events without input/output previews, which can carry
	// account data the widget never shows.
	const res = await fetch(args.endpoint, buildRequestInit(args, "text/event-stream", { trace: "minimal" }));

	if (!res.ok) {
		throw new Error(`Chat request failed: ${res.status} ${res.statusText}`);
	}
	if (!res.body) throw new Error("Chat stream had no body.");

	for await (const data of readSse(res.body)) {
		const event = parseEvent(data);
		if (!event) continue;
		if (event.type === "done") return event.done;
		if (event.type === "error") throw new Error(`Agent error: ${event.message}`);
		onEvent(event);
	}

	throw new Error("Chat stream ended before completing.");
}
